import { Plus } from 'lucide-react';
import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AI_REGIONS, type AiOrgSettings, aiApi, orgIsOn, regionLabel } from '@/lib/aiAdmin';

import { CENTER, TITLE, columns, confirmedWrite, LoadFailed, Loading, Toggle, useCursorList } from './common';

/** The stored settings of each organization. An organization with no row has the defaults and is not listed. */
export function OrganizationsTab() {
  const list = useCursorList(async (token) => {
    const page = await aiApi.orgSettings(token);
    return { rows: page.orgSettings ?? [], next: page.nextPageToken };
  }, []);
  const [orgId, setOrgId] = useState('');
  const [busy, setBusy] = useState(false);

  const change = async (org: AiOrgSettings, patch: { region?: string; enabled?: boolean; zeroRetention?: boolean }, question: string, done: string) => {
    setBusy(true);
    const ok = await confirmedWrite(
      question,
      () => aiApi.putOrgSettings(org.organizationId, { region: patch.region ?? org.region ?? 'global', enabled: patch.enabled, zeroRetention: patch.zeroRetention }),
      done,
      'The change failed',
    );
    setBusy(false);
    if (ok) list.reload();
  };

  const add = async () => {
    const id = orgId.trim();
    if (!id) return;
    await change({ organizationId: id, region: 'global' }, { enabled: true }, `Add settings for ${id}? It starts on, with the global region and no zero retention.`, `${id} added`);
    setOrgId('');
  };

  if (list.error) return <LoadFailed message={list.error} onRetry={list.reload} />;
  return (
    <DataTable
      toolbar={
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <Input value={orgId} onChange={(event) => setOrgId(event.target.value)} placeholder="Organization id" aria-label="Organization id" className="h-9 w-56" />
          <Button type="submit" size="sm" disabled={busy || !orgId.trim()}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Add organization
          </Button>
        </form>
      }
      footer={<TablePager {...list.pager} />}
    >
      <DataTableHead columns={columns(['Organization', 'AI is', 'Region', 'Zero retention'])} />
      <tbody>
        {list.rows.map((org) => {
          const on = orgIsOn(org);
          return (
            <DataTableRow key={org.organizationId}>
              <DataTableCell className={TITLE}>
                <span title={org.organizationId}>{org.organizationName || org.organizationId}</span>
              </DataTableCell>
              <DataTableCell className={CENTER}>
                <div className="inline-flex items-center gap-2">
                  <Toggle
                    checked={on}
                    label={`AI on for ${org.organizationId}`}
                    disabled={busy}
                    onChange={(next) =>
                      void change(
                        org,
                        { enabled: next },
                        next ? `Turn AI on for ${org.organizationId}?` : `Turn AI off for ${org.organizationId}? Every call of this organization is refused until you turn it on.`,
                        `AI is ${next ? 'on' : 'off'} for ${org.organizationId}`,
                      )
                    }
                  />
                  <span className="w-7 text-left text-xs text-muted-foreground">{on ? 'On' : 'Off'}</span>
                </div>
              </DataTableCell>
              <DataTableCell className={CENTER}>
                <Select
                  value={org.region ?? 'global'}
                  disabled={busy}
                  onValueChange={(region) =>
                    region !== (org.region ?? 'global') && void change(org, { region }, `Set the region of ${org.organizationId} to ${regionLabel(region)}?`, `${org.organizationId} uses ${regionLabel(region)}`)
                  }
                >
                  <SelectTrigger size="sm" className="mx-auto w-32" aria-label={`Region of ${org.organizationId}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_REGIONS.map((region) => (
                      <SelectItem key={region} value={region}>
                        {regionLabel(region)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DataTableCell>
              <DataTableCell className={CENTER}>
                <Toggle
                  checked={Boolean(org.zeroRetention)}
                  label={`Zero retention for ${org.organizationId}`}
                  disabled={busy}
                  onChange={(next) => void change(org, { zeroRetention: next }, `Turn zero retention ${next ? 'on' : 'off'} for ${org.organizationId}?`, `Zero retention is ${next ? 'on' : 'off'} for ${org.organizationId}`)}
                />
              </DataTableCell>
            </DataTableRow>
          );
        })}
        {list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={4}>
              <Loading label="Loading organizations" />
            </td>
          </tr>
        ) : null}
        {!list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
              No organization has stored settings. Each one uses the defaults: on, global region, no zero retention.
            </td>
          </tr>
        ) : null}
      </tbody>
    </DataTable>
  );
}
